import { useEffect, useState, useRef } from "react"
import { Button, Col, Container, Row, Spinner, Image, Stack } from "react-bootstrap"
import CustomLineChart from "./CustomLineChart"
import { FaArrowLeft, FaArrowRight } from "react-icons/fa"
import { requestWithAuthorization } from "../../network/apiRequests"
import { start } from "repl"
import { toPng } from "html-to-image"


export default function MonitoringChartContainer({ sensorId, last_timestamp }: { sensorId: string, last_timestamp: string }) {
    const chartContainerRef = useRef<HTMLDivElement>(null);

    const [data, setData] = useState<any[]>([])
    const [startDate, setStartDate] = useState<Date | null>(null)
    const [endDate, setEndDate] = useState<Date | null>(null)
    const [dataInterval, setDataInterval] = useState(60) // in minutes
    const [isLoading, setIsLoading] = useState(false)
    const [selectedDataAttribute, setSelectedDataAttribute] = useState("")
    const [chartData, setChartData] = useState<any[]>([])

    const handleDownload = () => {
        if (chartContainerRef.current) {
            toPng(chartContainerRef.current)
                .then((dataUrl) => {
                    const link = document.createElement('a');
                    link.download = `${selectedDataAttribute}-monitoring-chart.png`;
                    link.href = dataUrl;
                    link.click();
                })
                .catch((error) => {
                    console.error('Error generating PNG:', error);
                });
        }
    };

    async function fetchSensorData({ sensorId }: { sensorId: string }) {
        setIsLoading(true)
        let startAndEndParameters = ""
        let sortParameter = ""
        if (startDate && endDate) {
            startAndEndParameters = "&start=" + startDate.toISOString() + "&end=" + endDate.toISOString()
            sortParameter = "&sort=asc"
        }
        try {
            const response = await requestWithAuthorization(
                process.env.REACT_APP_API_HOSTNAME + "/recordingdata" + "?sensorId=" + sensorId + startAndEndParameters + sortParameter
            )
            if (response.status === 200) {
                if (startDate === null && endDate === null) {
                    // sort data ascending by timestamp
                    response.data.data.sort((a: any, b: any) => {
                        return a.timestamp - b.timestamp
                    })
                }
                setData(response.data.data)
            } else {
                console.log("Error fetching sensor data")
            }
        } catch (error) {
            console.error(error)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        fetchSensorData({ sensorId })
    }, [sensorId, startDate, endDate])

    useEffect(() => {
        console.log("last_timestamp", last_timestamp)
        if (last_timestamp) {
            const end = new Date(last_timestamp)
            // start is 1 hour before end
            const start = new Date(end.getTime() - 60 * 60 * 1000)
            setStartDate(start)
            setEndDate(end)
        }
    }, [last_timestamp])

    useEffect(() => {
        if (data.length > 0) {
            const firstKey = Object.keys(data[0])[0]
            setSelectedDataAttribute(firstKey)
        }
    }, [data])

    useEffect(() => {
        const chartData = data.map((entry) => ({
            x: entry.timestamp,
            [selectedDataAttribute]: entry[selectedDataAttribute]
        }))
        setChartData(chartData)
    }, [selectedDataAttribute, data])

    if (isLoading) {
        return (
            <Container fluid className="p-5 d-flex justify-content-center align-items-center">
                <Spinner animation="border" />
            </Container>
        )
    }

    if (data.length === 0) {
        return (
            <Container fluid className="p-5 d-flex justify-content-center align-items-center">
                <h3>No data available for sensor with ID: {sensorId}</h3>
                <p>Try again later or try to get data from a different time-period</p>
            </Container>
        )
    }

    const dataOptions = Object.keys(data[0])
    const dataSelector = (
        <Container fluid className="p-2 d-flex justify-content-end align-items-center">
            <label htmlFor="data-attribute-selector" className="me-2 visually-hidden">Select data attribute</label>
            <select 
                id="data-attribute-selector"
                aria-label="Select data attribute"
                onChange={(e) => {
                    setSelectedDataAttribute(e.target.value)
                }}
            >
                {dataOptions.map((option) => (
                    <option key={option} value={option}>{option}</option>
                ))}
            </select>
        </Container>
    )

    function fetchOlderData() {
        if (startDate === null || endDate === null) {
            return
        }
        setStartDate(new Date(startDate.getTime() - dataInterval * 60 * 1000))
        setEndDate(new Date(endDate.getTime() - dataInterval * 60 * 1000))
    }

    function fetchNewerData() {
        if (startDate === null || endDate === null) {
            return
        }
        setStartDate(new Date(startDate.getTime() + dataInterval * 60 * 1000))
        setEndDate(new Date(endDate.getTime() + dataInterval * 60 * 1000))
    }

    // TODO: add button to change interval

    return (
        <Container fluid className="px-0 pt-3">
            <Row className="d-flex">
                <Col>
                    <h1>{sensorId}</h1>
                    <p>Interval: {dataInterval} minutes</p>
                </Col>
                <Col>
                    {dataSelector}
                </Col>
            </Row>
            <Row className="pb-3">
                <Col className="d-flex">
                <Button className="wind-btn-outline" style={{width: "8em"}} onClick={fetchOlderData} disabled={isLoading}>
                    {isLoading ? <Spinner as="span" animation="border" size="sm" /> : <><FaArrowLeft /> Previous</>}
                </Button>
                <Button className="wind-btn-outline ms-2" style={{width: "8em"}} onClick={fetchNewerData} disabled={isLoading}>
                    {isLoading ? <Spinner as="span" animation="border" size="sm" /> : <>Next <FaArrowRight /></>}
                </Button>
                </Col>
            </Row>
            <Row>
                <Col>
                    <div ref={chartContainerRef}>
                        <Row className="mx-0 background-color-light rounded-corners p-3">
                            <Col>
                                {/* heading-row */}
                                <Row>
                                    <Col>
                                        <Row>
                                            <Col><span className="card-heading">{selectedDataAttribute}</span></Col>
                                        </Row>
                                    </Col>
                                    <Col>
                                        <Row className="align-items-center h-100">
                                            <Col className="d-flex justify-content-end">
                                                <Image
                                                    src="/img/logo_powered_by_windpuls_grey.png"
                                                    height={"50rem"}
                                                />
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>
                                {/* content-row */}
                                <Row className="pt-5">
                                    <Col>
                                        <CustomLineChart 
                                            plotData={chartData} 
                                            isLoading={isLoading} 
                                            xKey="x" 
                                            yKey={[selectedDataAttribute]} 
                                            onDownload={handleDownload}
                                        />
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </div>
                </Col>
            </Row>
        </Container>
    )
}
